import { Head, Link } from "@inertiajs/react";
import { useMemo, useState } from "react";
import { salonpageR2ImageUrl, salonpageStaffVideoUrl, salonpageVideoMp4Url, encodeSalonpagePathId } from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";

const PER_PAGE = 48;

interface StaffItem {
  id: string;
  name: string;
  kana: string;
  role: string;
  history: string;
  description: string;
  imageUrl: string;
  position: number;
  salonId: string;
  salonName: string;
  profileVideoId?: string | null;
}

interface Summary {
  totalStaff: number;
  totalProfileVideos: number;
  totalSalons: number;
}

interface Meta {
  title: string;
  description: string;
}

interface Props {
  staff?: StaffItem[];
  summary?: Summary;
  meta?: Meta;
}

const EMPTY_SUMMARY: Summary = {
  totalStaff: 0,
  totalProfileVideos: 0,
  totalSalons: 0,
};

export default function StaffsIndex({ staff = [], summary = EMPTY_SUMMARY, meta }: Props) {
  const [query, setQuery] = useState("");
  const [videoOnly, setVideoOnly] = useState(true);
  const [page, setPage] = useState(1);
  const normalizedQuery = query.trim().toLowerCase();

  const filteredStaff = useMemo(() => {
    return staff.filter((member) => {
      if (videoOnly && !member.profileVideoId) return false;
      if (!normalizedQuery) return true;
      const haystack = [
        member.name,
        member.kana,
        member.role,
        member.history,
        member.description,
        member.salonName,
        member.salonId,
        member.id,
      ].join(" ").toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [staff, videoOnly, normalizedQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredStaff.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pagedStaff = filteredStaff.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  function updateQuery(value: string) {
    setQuery(value);
    setPage(1);
  }

  function updateVideoOnly(value: boolean) {
    setVideoOnly(value);
    setPage(1);
  }

  return (
    <>
      <Head title={meta?.title ?? "スタッフ一覧 | Hairbook Salon"}>
        {meta?.description && <meta name="description" content={meta.description} />}
      </Head>

      <div className="min-h-screen bg-[#f6f6f3] text-zinc-950 antialiased">
        <header className="sticky top-0 z-20 border-b border-zinc-200 bg-white/90 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
            <Link href="/salons" className="min-w-0">
              <p className="text-base font-black tracking-tight">Salon Pages</p>
              <p className="text-[11px] font-bold text-zinc-400">Hairbook</p>
            </Link>
            <nav className="flex items-center gap-4 text-xs font-bold text-zinc-500">
              <Link href="/salons" className="hover:text-zinc-950">サロン</Link>
              <a href="https://hairbook.jp/" className="hidden hover:text-zinc-950 sm:inline">hairbook.jp</a>
            </nav>
          </div>
        </header>

        <main className="mx-auto max-w-6xl px-4 py-5 sm:py-8">
          <section className="grid gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <div>
              <h1 className="text-2xl font-black tracking-tight sm:text-3xl">スタッフ一覧</h1>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-600">
                プロフィール動画とスタッフ情報をまとめて確認できます。
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2 rounded-lg border border-zinc-200 bg-white p-2 shadow-sm">
              <SummaryItem label="staff" value={summary.totalStaff} />
              <SummaryItem label="videos" value={summary.totalProfileVideos} />
              <SummaryItem label="salons" value={summary.totalSalons} />
            </div>
          </section>

          <section className="mt-5 rounded-lg border border-zinc-200 bg-white p-3 shadow-sm">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <label>
                <span className="sr-only">スタッフ検索</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => updateQuery(event.target.value)}
                  placeholder="スタッフ名、サロン名、HPB ID で検索"
                  className="h-11 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm outline-none focus:border-zinc-900"
                />
              </label>
              <label className="flex h-11 items-center justify-between gap-3 rounded-md border border-zinc-300 px-3 text-sm font-bold text-zinc-700 sm:min-w-40">
                <span>動画あり</span>
                <input
                  type="checkbox"
                  checked={videoOnly}
                  onChange={(event) => updateVideoOnly(event.target.checked)}
                  className="h-4 w-4 accent-zinc-950"
                />
              </label>
            </div>
            <p className="mt-3 text-sm font-bold text-zinc-500">
              <span className="tabular-nums text-zinc-950">{filteredStaff.length.toLocaleString()}</span> / {staff.length.toLocaleString()} 件
            </p>
          </section>

          {pagedStaff.length > 0 ? (
            <>
              <section className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {pagedStaff.map((member) => (
                  <StaffCard key={member.id} member={member} />
                ))}
              </section>
              {totalPages > 1 && (
                <nav className="mt-6 flex items-center justify-center gap-1">
                  <PageButton disabled={currentPage <= 1} onClick={() => setPage(1)}>«</PageButton>
                  <PageButton disabled={currentPage <= 1} onClick={() => setPage((value) => value - 1)}>‹</PageButton>
                  {paginationRange(currentPage, totalPages).map((item) =>
                    item === "..." ? (
                      <span key={item} className="px-2 text-sm text-zinc-400">…</span>
                    ) : (
                      <PageButton key={item} active={item === currentPage} onClick={() => setPage(item as number)}>{item}</PageButton>
                    )
                  )}
                  <PageButton disabled={currentPage >= totalPages} onClick={() => setPage((value) => value + 1)}>›</PageButton>
                  <PageButton disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>»</PageButton>
                </nav>
              )}
            </>
          ) : (
            <section className="mt-4 grid min-h-48 place-items-center rounded-lg border border-dashed border-zinc-300 bg-white">
              <p className="text-sm font-bold text-zinc-400">該当するスタッフがいません。</p>
            </section>
          )}
        </main>
      </div>
    </>
  );
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="min-w-[4.5rem] rounded-md bg-zinc-50 px-3 py-2 text-center">
      <p className="text-lg font-black tabular-nums">{value.toLocaleString()}</p>
      <p className="mt-0.5 text-[10px] font-bold uppercase text-zinc-400">{label}</p>
    </div>
  );
}

function StaffCard({ member }: { member: StaffItem }) {
  return (
    <Link
      href={`/staffs/${encodeSalonpagePathId(member.id)}`}
      className="group block rounded-lg border border-zinc-200 bg-white p-2 shadow-sm transition hover:-translate-y-0.5 hover:border-zinc-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2"
    >
      <StaffCardMedia member={member} />
      <div className="pt-2">
        <p className="line-clamp-1 text-[11px] font-bold text-zinc-400">{member.salonName}</p>
        <h2 className="mt-1 line-clamp-1 text-sm font-black leading-5">{member.name}</h2>
        <p className="mt-1 line-clamp-1 text-xs font-bold text-zinc-500">{member.history || member.role || "スタッフ"}</p>
        {member.description && <p className="mt-1 line-clamp-2 text-xs leading-5 text-zinc-500">{member.description}</p>}
      </div>
    </Link>
  );
}

function StaffCardMedia({ member }: { member: StaffItem }) {
  const posterUrl = member.profileVideoId
    ? salonpageR2ImageUrl(member.salonId, member.id, "thumbnail")
    : member.imageUrl || salonpageR2ImageUrl(member.salonId, member.id, "thumbnail");
  const videoUrl = member.profileVideoId ? salonpageVideoMp4Url(member.profileVideoId) : salonpageStaffVideoUrl(member.salonId, member.id);

  return (
    <StaffCardVideo
      videoUrl={videoUrl}
      posterUrl={posterUrl}
      alt={member.name}
    />
  );
}

function StaffCardVideo({ videoUrl, posterUrl, alt }: { videoUrl: string; posterUrl: string; alt: string }) {
  const [hasError, setHasError] = useState(false);
  const { videoRef } = useVideoPlayer({
    src: hasError ? null : videoUrl,
    poster: posterUrl,
    muted: true,
    loop: true,
  });

  if (hasError) {
    return <img src={posterUrl} alt={alt} className="aspect-[9/16] w-full rounded-md bg-zinc-100 object-cover object-top" loading="lazy" />;
  }

  return (
    <div className="aspect-[9/16] overflow-hidden rounded-md bg-zinc-100">
      <video
        ref={videoRef}
        aria-label={alt}
        playsInline
        muted
        loop
        preload="metadata"
        poster={posterUrl}
        onError={() => setHasError(true)}
        className="h-full w-full object-cover object-top"
      />
    </div>
  );
}

function PageButton({ children, active = false, disabled = false, onClick }: { children: React.ReactNode; active?: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "grid h-9 min-w-9 place-items-center rounded-md border px-2 text-sm font-bold transition",
        active ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:text-zinc-950",
        disabled ? "pointer-events-none opacity-40" : "",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function paginationRange(current: number, total: number): Array<number | "..."> {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((page) => page >= 1 && page <= total).sort((left, right) => left - right);
  const result: Array<number | "..."> = [];
  for (const page of sorted) {
    const previous = result[result.length - 1];
    if (typeof previous === "number" && page - previous > 1) result.push("...");
    result.push(page);
  }
  return result;
}
